import type { CatalogPlatform, PublicPlatform } from "./api/types";

/** Steps shown when a platform ships none (custom addresses, mostly). */
export const FALLBACK_STEPS = [
  "열린 화면에서 후기 쓰기를 누르세요",
  "글 칸을 길게 눌러 붙여 넣으세요",
  "별점은 직접 고르고 올리기를 누르세요",
];

export const CUSTOM_PLATFORM_ID = "custom";
/** Same cap as the server (`URL_MAX`). */
export const URL_MAX = 500;

/** `example.com` matches `example.com` and `*.example.com`, never `badexample.com`. */
export function hostMatches(host: string, hints: readonly string[]): boolean {
  const h = host.toLowerCase().replace(/\.$/, "");
  return hints.some((hint) => {
    const x = hint.toLowerCase();
    return h === x || h.endsWith(`.${x}`);
  });
}

export function needsUrl(p: Pick<CatalogPlatform, "id" | "link">): boolean {
  return p.id === CUSTOM_PLATFORM_ID || p.link !== "guide";
}

/** Helper line under a URL field, e.g. "apps.apple.com 으로 시작하는 주소예요". */
export function urlHint(p: Pick<CatalogPlatform, "id" | "link" | "url_hosts" | "name">): string {
  if (!needsUrl(p)) return "주소 없이 고객에게 들어가는 순서만 안내해요.";
  if (p.url_hosts.length === 0) return "https:// 로 시작하는 주소를 넣어 주세요.";
  return `${p.url_hosts.join(", ")} 주소를 넣어 주세요. 우리 매장이나 상품의 후기 화면 주소예요.`;
}

/** Returns a 해요체 error message, or null when the address is acceptable. */
export function validatePlatformUrl(
  p: Pick<CatalogPlatform, "id" | "link" | "url_hosts" | "name">,
  raw: string
): string | null {
  const value = raw.trim();
  if (!value) return needsUrl(p) ? `${p.name} 주소를 넣어 주세요.` : null;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return "주소 모양이 아니에요. https:// 부터 통째로 붙여 넣어 주세요.";
  }
  if (url.protocol !== "https:") return "https:// 로 시작하는 주소만 쓸 수 있어요.";
  if (value.length > URL_MAX) return `주소가 너무 길어요. ${URL_MAX}자 안으로 줄여 주세요.`;
  if (p.url_hosts.length > 0 && !hostMatches(url.hostname, p.url_hosts)) {
    return `${p.name} 주소가 아닌 것 같아요. ${p.url_hosts[0]} 주소인지 확인해 주세요.`;
  }
  return null;
}

/**
 * Mirror of the server's `open_url` (api/catalog/platforms.py), used only by the console
 * preview: guide platforms have no address; any other platform with a `write_suffix` gets it
 * appended once ("&" when the address already has a query). The customer page never calls
 * this, because `PublicPage.platforms[].url` already carries the suffix.
 */
export function serverOpenUrl(
  p: Pick<CatalogPlatform, "link" | "write_suffix">,
  url: string | null | undefined
): string | null {
  if (url == null || p.link === "guide") return null;
  const suffix = p.write_suffix;
  if (suffix && !url.includes(suffix)) return `${url}${url.includes("?") ? "&" : "?"}${suffix}`;
  return url;
}

/** Where the "open" button goes. Guide platforms (and missing addresses) have nowhere to go. */
export function openUrlFor(p: Pick<PublicPlatform, "link" | "url">): string | null {
  if (p.link === "guide") return null;
  return p.url?.trim() ? p.url : null;
}

export function stepsFor(p: Pick<PublicPlatform, "steps">): string[] {
  return p.steps.length > 0 ? p.steps : FALLBACK_STEPS;
}

export function platformButtonLabel(p: Pick<PublicPlatform, "name">): string {
  return `${p.name}에 남기기`;
}

/** Characters beyond the platform limit (0 when it fits or there is no limit). */
export function overflowBy(text: string, bodyMax: number | null | undefined): number {
  if (!bodyMax) return 0;
  return Math.max(0, Array.from(text).length - bodyMax);
}

export function charCount(text: string): number {
  return Array.from(text).length;
}
